import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Button from '../components/Button'
import MultiSelect from '../components/MultiSelect'
import Icon from '../components/Icon'
import { useClassStore } from '../../stores/classesStore'
import { useSchoolStore } from '../../stores/schoolsStore'
import { useUsersStore } from '../../stores/usersStore'
import { useStudentsStore } from '../../stores/studentsStore'
import { School, User, Student } from '../../types'
import { CreateClassRequest } from '../../services/classes'

const ClassEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const {
    schoolClasses,
    schoolClassesLoading,
    schoolClassesError,
    isCreating,
    isUpdating,
    fetchSchoolClasses,
    addClass,
    editClass,
    removeClass,
    clearSchoolClassesError,
  } = useClassStore()

  const { schools, loading: schoolsLoading, fetchSchools } = useSchoolStore()
  const { users, loading: usersLoading, fetchUsers } = useUsersStore()
  const {
    students,
    loading: studentsLoading,
    fetchStudents,
  } = useStudentsStore()

  const [className, setClassName] = useState('')
  const [selectedSchool, setSelectedSchool] = useState('')
  const [selectedTeachers, setSelectedTeachers] = useState<string[]>([])
  const [selectedStudents, setSelectedStudents] = useState<string[]>([])

  const isNewClass = id === 'new'
  const currentClass = !isNewClass
    ? schoolClasses.find((c) => c._id === id)
    : null

  useEffect(() => {
    if (schoolClasses.length === 0) {
      fetchSchoolClasses()
    }
    if (schools.length === 0) {
      fetchSchools()
    }
    if (users.length === 0) {
      fetchUsers()
    }
    if (students.length === 0) {
      fetchStudents()
    }
  }, [
    schoolClasses.length,
    schools.length,
    users.length,
    students.length,
    fetchSchoolClasses,
    fetchSchools,
    fetchUsers,
    fetchStudents,
  ])

  useEffect(() => {
    if (!isNewClass && currentClass) {
      setClassName(currentClass.name)

      const schoolId =
        typeof currentClass.school === 'object'
          ? currentClass.school._id
          : currentClass.school
      setSelectedSchool(schoolId)

      // Set teachers - handle both populated and non-populated cases
      const teacherIds = Array.isArray(currentClass.teachers)
        ? currentClass.teachers.map((teacher: any) =>
            typeof teacher === 'string' ? teacher : teacher._id,
          )
        : []
      setSelectedTeachers(teacherIds)

      // Set students - handle both populated and non-populated cases
      const studentIds = Array.isArray(currentClass.students)
        ? currentClass.students.map((student: any) =>
            typeof student === 'string' ? student : student._id,
          )
        : []
      setSelectedStudents(studentIds)
    }
  }, [isNewClass, currentClass])

  useEffect(() => {
    return () => {
      clearSchoolClassesError()
    }
  }, [clearSchoolClassesError])

  const handleSaveClass = async () => {
    if (!className.trim() || !selectedSchool) {
      return
    }

    try {
      const classData: CreateClassRequest = {
        name: className.trim(),
        school: selectedSchool,
        teachers: selectedTeachers,
        students: selectedStudents,
      }

      if (isNewClass) {
        await addClass(classData)
      } else if (id) {
        await editClass(id, classData)
      }

      navigate('/manage/classes')
    } catch (err) {
      console.log(
        isNewClass ? 'Error creating class:' : 'Error updating class:',
        err,
      )
    }
  }

  const handleDeleteClass = async () => {
    if (!currentClass) return

    if (
      window.confirm(
        `Are you sure you want to delete "${currentClass.name}"? This action cannot be undone.`,
      )
    ) {
      try {
        await removeClass(currentClass._id)
        navigate('/manage/classes')
      } catch (err) {
        console.log('Error deleting class:', err)
      }
    }
  }

  // Transform users to MultiSelectOption format
  const getAvailableTeachers = () => {
    return users
      .filter((user: User) => {
        if (!user.schools || user.schools.length === 0) return false
        return user.schools.some((school: any) =>
          typeof school === 'object'
            ? school._id === selectedSchool
            : school === selectedSchool,
        )
      })
      .map((user: User) => ({
        _id: user._id,
        name: `${user.firstName} ${user.lastName} (${user.email})`,
      }))
  }

  // Transform students to MultiSelectOption format, filtered by selected school
  const getAvailableStudents = () => {
    if (!selectedSchool) return []

    return students
      .filter((student: Student) => {
        const studentSchoolId =
          typeof student.school === 'object'
            ? student.school._id
            : student.school
        return studentSchoolId === selectedSchool
      })
      .map((student: Student) => ({
        _id: student._id,
        name: `${student.firstName} ${student.lastName}${student.studentId ? ` (${student.studentId})` : ''}`,
      }))
  }

  if (
    (schoolClassesLoading && schoolClasses.length === 0) ||
    (schoolsLoading && schools.length === 0) ||
    (usersLoading && users.length === 0) ||
    (studentsLoading && students.length === 0)
  ) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!isNewClass && !currentClass && !schoolClassesLoading) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          Class not found
        </div>
        <Button
          onClick={() => navigate('/manage/classes')}
          variant="link"
          className="mt-4 flex items-center gap-2"
        >
          <Icon type="arrow-left" />
          Back to Classes
        </Button>
      </div>
    )
  }

  return (
    <div className="mx-auto space-y-6">
      {/* Header with Back Button */}
      <div>
        <Button
          onClick={() => navigate('/manage/classes')}
          variant="link"
          className="mb-4 flex items-center gap-2"
        >
          <Icon type="arrow-left" />
          Back to Classes
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isNewClass ? 'Create New Class' : 'Edit Class'}
        </h1>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Class Name
          </label>
          <input
            type="text"
            value={className}
            onChange={(e) => setClassName(e.target.value)}
            placeholder="Enter class name"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            autoFocus
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            School
          </label>
          <select
            value={selectedSchool}
            onChange={(e) => {
              setSelectedSchool(e.target.value)
              // Clear selected students when school changes
              setSelectedStudents([])
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select a school</option>
            {schools.map((school: School) => (
              <option key={school._id} value={school._id}>
                {school.displayName}
              </option>
            ))}
          </select>
        </div>

        {/* Teachers multi-select */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Teachers
          </label>
          <MultiSelect
            options={getAvailableTeachers()}
            selectedValues={selectedTeachers}
            onChange={setSelectedTeachers}
            placeholder={
              selectedSchool
                ? 'Select teachers for this class...'
                : 'Select a school first to see available teachers'
            }
            disabled={!selectedSchool}
            className="w-full"
            fixed={true}
          />
          <p className="mt-1 text-xs text-gray-500">
            {selectedSchool
              ? 'Select which users will be teachers for this class'
              : 'Teachers will be filtered by the selected school'}
          </p>
        </div>

        {/* Students multi-select */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Students
          </label>
          <MultiSelect
            options={getAvailableStudents()}
            selectedValues={selectedStudents}
            onChange={setSelectedStudents}
            placeholder={
              selectedSchool
                ? 'Select students for this class...'
                : 'Select a school first to see available students'
            }
            disabled={!selectedSchool}
            className="w-full"
            fixed={true}
          />
          <p className="mt-1 text-xs text-gray-500">
            {selectedSchool
              ? `${getAvailableStudents().length} students available in this school`
              : 'Students will be filtered by the selected school'}
          </p>
        </div>

        {schoolClassesError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
            {schoolClassesError}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-4 border-t">
          <div>
            {!isNewClass && (
              <Button
                onClick={handleDeleteClass}
                disabled={isUpdating}
                variant="link"
                className="px-4 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg"
              >
                Delete Class
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate('/manage/classes')}
              disabled={isCreating || isUpdating}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveClass}
              disabled={
                isCreating || isUpdating || !className.trim() || !selectedSchool
              }
              variant="primary"
              loading={isCreating || isUpdating}
            >
              {isNewClass ? 'Create Class' : 'Update Class'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClassEdit
